/**
 * Copying text, including where the browser will not give us the modern API.
 *
 * `navigator.clipboard` exists only in a secure context. This application is
 * served over plain HTTP on a server address, so on the deployment people
 * actually use, `navigator.clipboard` is `undefined` — and
 * `navigator.clipboard.writeText(…)` therefore throws a TypeError before it
 * gets anywhere near the clipboard. Both Copy buttons did nothing at all, with
 * no error a reader could see, on every origin except localhost.
 *
 * So: the real API when it is there, and the deprecated `execCommand` when it
 * is not. `execCommand("copy")` is deprecated but it is not going anywhere,
 * every browser still implements it, and it is the only thing that works
 * outside a secure context. Returning a boolean rather than swallowing the
 * failure matters too — a button that says "Copied" when nothing was copied is
 * worse than one that does nothing, because the reader finds out later, in a
 * paste, somewhere else.
 *
 * The real fix is TLS. This makes the button honest until there is some.
 */
export async function copyText(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission refused, or a browser that exposes the API and blocks it.
      // Fall through rather than give up: the old path often still works.
    }
  }

  const holder = document.createElement("textarea");
  holder.value = text;
  // Off-screen rather than hidden: `display:none` and `visibility:hidden`
  // cannot be selected, and an unselected textarea copies nothing.
  holder.setAttribute("readonly", "");
  holder.style.position = "fixed";
  holder.style.top = "-1000px";
  holder.style.opacity = "0";
  document.body.appendChild(holder);
  try {
    holder.select();
    holder.setSelectionRange(0, text.length);
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(holder);
  }
}
